import { useState, useEffect, useCallback } from "react";
import { authHeaders } from "../lib/auth";
import AppHeader from "./AppHeader";

// ── Colors (matches ProfileScreen / MyIntake / DevicesScreen) ─────────
const C = {
  white:"#FFFFFF", bg:"#F8FAFB", border:"#E8EEF2",
  blue:"#51B0E6", blueDark:"#2A8FCA", blueLight:"#EDF6FC",
  body:"#1A2535", muted:"#6B7A8D",
  green:"#1E8A4C", warning:"#F29423", danger:"#D93025",
};

const API_BASE = 'https://generosity-sales-engine-mvp-api.onrender.com';
// Phase 2: identity rides via authHeaders() (X-WTR-Auth session token).

// Lifestyle = baseline activity level. Maps 1:1 to the `activity` enum the
// server-side calc (routes/wtrHydration.js calcNHS) keys off. Drives the target.
const LIFESTYLE = [["sedentary","Sedentary"],["light","Light"],["moderate","Moderate"],["active","Active"],["athlete","Athlete"]];

// Climate maps 1:1 to the `climate` enum. Drives the target. Manual for now;
// auto-from-ZIP needs a cached server weather endpoint (next backend task).
const CLIMATE = [["cool","Cool"],["mild","Mild"],["warm","Warm"],["hot","Hot"],["extreme","Extreme"]];

// Activity TYPES the user actually does. The calc does not use type, it uses
// exercise INTENSITY, so we derive intensity from the hardest type selected and
// store that in the existing `exercise` column. The type list itself persists in
// the new activity_types column so the selection survives reloads.
// Third element is the intensity rank: 1 light, 2 moderate, 3 intense.
const ACTIVITY_TYPES = [
  ["walking","Walking",1],
  ["running","Running",3],
  ["cycling","Cycling",2],
  ["swimming","Swimming",2],
  ["strength","Strength",2],
  ["hiit","HIIT / Cardio",3],
  ["tennis","Tennis",2],
  ["golf","Golf",1],
  ["hiking","Hiking",2],
  ["yoga","Yoga / Pilates",1],
];
const RANK_TO_EX = { 0:"none", 1:"light", 2:"moderate", 3:"intense" };
function deriveExercise(types) {
  if (!Array.isArray(types) || types.length === 0) return "none";
  let max = 0;
  for (const t of types) {
    const f = ACTIVITY_TYPES.find(a => a[0] === t);
    if (f && f[2] > max) max = f[2];
  }
  return RANK_TO_EX[max] || "none";
}

// Areas of focus. Does NOT drive the hydration target; personalizes guidance.
const GOALS = [
  ["lose_weight","Lose weight"],
  ["build_muscle","Build muscle"],
  ["move_more","Move more"],
  ["sleep_recovery","Sleep & recovery"],
  ["athletic","Athletic performance"],
  ["wellness","General wellness"],
  ["hydration","Stay hydrated"],
];

function HIcon({ name, size=20, color=C.blue }) {
  const s=size, ic=color;
  const icons = {
    pulse: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M3 12h4l2.5-7 4 14 2.5-7H21" stroke={ic} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    target: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke={ic} strokeWidth="1.5"/><circle cx="12" cy="12" r="5" stroke={ic} strokeWidth="1.3"/><circle cx="12" cy="12" r="1.6" fill={ic}/></svg>,
    activity: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M13 3l-2 9h5l-2 9" stroke={ic} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>,
    ring: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="7" stroke={ic} strokeWidth="2"/></svg>,
    goal: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><path d="M5 22V4" stroke={ic} strokeWidth="1.6" strokeLinecap="round"/><path d="M5 4.5h11l-2.2 3.8L16 12H5" stroke={ic} strokeWidth="1.6" strokeLinejoin="round" fill="none"/></svg>,
    climate: <svg width={s} height={s} viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="4" stroke={ic} strokeWidth="1.6"/><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" stroke={ic} strokeWidth="1.6" strokeLinecap="round"/></svg>,
  };
  return <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center"}}>{icons[name]||icons.pulse}</span>;
}

function SectionCard({ icon, title, sub, children }) {
  return (
    <div style={{background:C.white,borderRadius:18,border:`1.5px solid ${C.border}`,padding:"16px"}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:children?14:0}}>
        <div style={{width:38,height:38,borderRadius:11,flexShrink:0,background:C.blueLight,display:"flex",alignItems:"center",justifyContent:"center"}}>
          <HIcon name={icon} size={20} color={C.blueDark}/>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:15,fontWeight:800,color:C.body}}>{title}</div>
          {sub && <div style={{fontSize:11,color:C.muted,marginTop:1}}>{sub}</div>}
        </div>
      </div>
      {children}
    </div>
  );
}

// Single-select chips (Lifestyle, Climate)
function Chips({ value, onChange, options }) {
  return (
    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
      {options.map(([v,l]) => (
        <button key={v} onClick={()=>onChange(v)} style={{
          padding:"8px 15px", borderRadius:20,
          border:`1.5px solid ${value===v?C.blue:C.border}`,
          background: value===v?C.blueLight:C.white,
          color: value===v?C.blue:C.muted,
          fontSize:13, fontWeight: value===v?700:500,
          cursor:"pointer", fontFamily:"inherit",
        }}>{l}</button>
      ))}
    </div>
  );
}

// Multi-select chips (Health Goals, Activity types)
function MultiChips({ values, onToggle, options }) {
  const sel = Array.isArray(values) ? values : [];
  return (
    <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
      {options.map((opt) => {
        const v = opt[0], l = opt[1];
        const on = sel.includes(v);
        return (
          <button key={v} onClick={()=>onToggle(v)} style={{
            padding:"8px 15px", borderRadius:20,
            border:`1.5px solid ${on?C.blue:C.border}`,
            background: on?C.blueLight:C.white,
            color: on?C.blue:C.muted,
            fontSize:13, fontWeight: on?700:500,
            cursor:"pointer", fontFamily:"inherit",
          }}>{on ? `\u2713 ${l}` : l}</button>
        );
      })}
    </div>
  );
}

function BioStat({ label, value, unit }) {
  return (
    <div style={{flex:1,textAlign:"center",padding:"12px 6px",background:C.bg,borderRadius:12}}>
      <div style={{fontSize:24,fontWeight:900,color: value!=null ? C.body : C.muted,fontFamily:"Nunito,sans-serif",lineHeight:1}}>
        {value!=null ? value : "\u2014"}
      </div>
      {unit && value!=null && <div style={{fontSize:10,color:C.muted,fontWeight:700,marginTop:2}}>{unit}</div>}
      <div style={{fontSize:10,fontWeight:700,color:C.muted,letterSpacing:0.5,textTransform:"uppercase",marginTop:6}}>{label}</div>
    </div>
  );
}

// One wearable source row inside Biometric Insights
function SourceRow({ name, detail, tone }) {
  const col = tone === "on" ? C.green : tone === "soon" ? C.warning : C.muted;
  const label = tone === "on" ? "Connected" : tone === "soon" ? "iOS app" : "Not connected";
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 0",borderTop:`1px solid ${C.border}`}}>
      <div style={{minWidth:0}}>
        <div style={{fontSize:13,fontWeight:700,color:C.body}}>{name}</div>
        {detail && <div style={{fontSize:11,color:C.muted,marginTop:1,lineHeight:1.4}}>{detail}</div>}
      </div>
      <span style={{
        flexShrink:0, background:`${col}14`, border:`1px solid ${col}30`,
        borderRadius:20, padding:"3px 10px", fontSize:10, fontWeight:700,
        color:col, letterSpacing:0.5, textTransform:"uppercase", whiteSpace:"nowrap",
      }}>{label}</span>
    </div>
  );
}

export default function MyHealth({ onClose }) {
  const [email] = useState(() => {
    try { const p = JSON.parse(localStorage.getItem('wtr_profile') || '{}'); return (p.email || '').trim().toLowerCase(); }
    catch(e) { return ''; }
  });

  const [profile, setProfile] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  // Editable inputs
  const [goals, setGoals] = useState([]);             // health_goals (does not drive target)
  const [lifestyle, setLifestyle] = useState("moderate"); // activity enum (drives target)
  const [activityTypes, setActivityTypes] = useState([]); // activity_types (drives target via derived intensity)
  const [exerciseMins, setExerciseMins] = useState("45");
  const [climate, setClimate] = useState("mild");

  const validEmail = email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const hasName = !!(profile && profile.first_name && profile.last_name);

  const loadSummary = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/api/wtr/hydration/summary?units=l&email=${encodeURIComponent(email)}`, { headers: authHeaders() });
      if (!r.ok) return;
      const j = await r.json();
      if (j.ok) setSummary(j);
    } catch(e) { /* leave prior */ }
  }, [email]);

  useEffect(() => {
    if (!validEmail) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const pr = await fetch(`${API_BASE}/api/wtr/profile?email=${encodeURIComponent(email)}`, { headers: authHeaders() });
        const pd = await pr.json();
        if (!cancelled && pd.ok && pd.profile) {
          const p = pd.profile;
          setProfile(p);
          if (p.activity) setLifestyle(p.activity);
          if (p.exercise_mins != null) setExerciseMins(String(p.exercise_mins));
          if (p.climate) setClimate(p.climate);
          if (Array.isArray(p.health_goals)) setGoals(p.health_goals);
          if (Array.isArray(p.activity_types)) setActivityTypes(p.activity_types);
        }
      } catch(e) { /* leave defaults */ }
      await loadSummary();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [email, validEmail, loadSummary]);

  function toggleGoal(v) {
    setGoals(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  }
  function toggleActivity(v) {
    setActivityTypes(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v]);
  }

  async function saveHealth() {
    if (!hasName) return;
    setSaving(true); setSaveError("");
    try {
      const res = await fetch(`${API_BASE}/api/wtr/profile`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          email,
          first_name: profile.first_name,
          last_name: profile.last_name,
          activity: lifestyle,
          climate,
          exercise: deriveExercise(activityTypes), // derived intensity drives the calc
          exercise_mins: exerciseMins,
          health_goals: goals,
          activity_types: activityTypes,
        }),
      });
      const d = await res.json();
      if (!res.ok || !d.ok) { setSaveError(d.error || 'Could not save. Try again.'); setSaving(false); return; }
      await loadSummary(); // target reflects the new inputs
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
    } catch(e) {
      setSaveError('Network error. Try again.');
    }
    setSaving(false);
  }

  const bio = summary?.biometrics || null;
  const bioConnected = !!summary?.biometricsConnected;
  const bioBonus = summary?.nhs?.bioBonus || 0;
  const targetL = summary?.intake?.targetL != null ? summary.intake.targetL : null;
  const targetOz = targetL != null ? Math.round(targetL * 33.814) : null;
  const breakdown = summary?.targetBreakdown || [];
  const derivedEx = deriveExercise(activityTypes);

  if (loading) {
    return (
      <div style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",display:"flex",alignItems:"center",justifyContent:"center"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:40,height:40,border:`3px solid ${C.border}`,borderTopColor:C.blue,borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 12px"}}/>
          <div style={{fontSize:14,color:C.muted,fontWeight:600}}>Loading your health...</div>
          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="my-health-screen" style={{fontFamily:"Nunito Sans,-apple-system,sans-serif",background:C.white,minHeight:"100%",color:C.body,display:"flex",flexDirection:"column",maxWidth:480,margin:"0 auto",WebkitFontSmoothing:"antialiased"}}>
      <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@700;800;900&family=Nunito+Sans:wght@400;600;700&display=swap" rel="stylesheet"/>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}} @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}} *{box-sizing:border-box;-webkit-tap-highlight-color:transparent} input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}`}</style>

      <AppHeader
        variant="modal"
        title="My Health"
        onClose={onClose}
      />

      <div style={{flex:1,overflowY:"auto",padding:"20px",display:"flex",flexDirection:"column",gap:16,paddingBottom:40,animation:"fadeIn 0.3s ease"}}>

        {!hasName && (
          <div style={{padding:"16px 18px",background:`${C.warning}10`,border:`1.5px solid ${C.warning}30`,borderRadius:16,fontSize:13,fontWeight:600,color:C.body}}>
            Set up your Profile first. Your health settings are saved to your profile, so we need your name and email before saving.
          </div>
        )}

        {/* 1. BIOMETRIC INSIGHTS (Oura live, Apple Watch walled, more coming) */}
        <SectionCard icon="ring" title="Biometric Insights" sub="Live signals personalizing your hydration">
          {bioConnected ? (
            <div style={{marginBottom:6}}>
              <div style={{display:"flex",gap:10}}>
                <BioStat label="Readiness" value={bio?.readiness_score ?? null} />
                <BioStat label="Sleep" value={bio?.sleep_score ?? null} />
                <BioStat label="HRV" value={bio?.hrv_avg ?? null} unit="ms" />
              </div>
              <div style={{fontSize:12,color:C.muted,lineHeight:1.5,marginTop:12}}>
                {bioBonus > 0
                  ? `Your biometrics are personalizing your target and adding +${bioBonus} to your hydration score today.`
                  : "Your biometrics are personalizing your hydration target today."}
              </div>
            </div>
          ) : (
            <div style={{fontSize:13,color:C.muted,lineHeight:1.6,marginBottom:6}}>
              Connect a wearable in Devices & Integrations to personalize your hydration with sleep, HRV, and readiness. Without one, your target uses your body metrics and lifestyle alone.
            </div>
          )}

          {/* Sources, in sensible order: working first, then walled, then horizon */}
          <SourceRow name="Oura Ring" detail="Sleep, readiness, HRV, recovery" tone={bioConnected ? "on" : "off"} />
          <SourceRow name="Apple Watch" detail="Available in the upcoming iOS app" tone="soon" />
          <div style={{paddingTop:10,borderTop:`1px solid ${C.border}`,fontSize:11,color:C.muted,lineHeight:1.5}}>
            Whoop, Garmin, and Fitbit support is on the roadmap. Manage connections in Devices & Integrations.
          </div>
        </SectionCard>

        {/* 2. HYDRATION TARGET (the computed output) */}
        <SectionCard icon="target" title="Your Hydration Target" sub="Why your daily target is what it is">
          <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:14}}>
            <span style={{fontSize:36,fontWeight:900,color:C.body,fontFamily:"Nunito,sans-serif",lineHeight:1}}>
              {targetL != null ? targetL.toFixed(1) : "\u2014"}
            </span>
            <span style={{fontSize:14,fontWeight:700,color:C.muted}}>L / day</span>
            {targetOz != null && <span style={{fontSize:12,color:C.muted,marginLeft:"auto"}}>{targetOz} oz</span>}
          </div>
          {breakdown.length > 0 ? (
            <div style={{display:"flex",flexDirection:"column",gap:0,borderTop:`1px solid ${C.border}`}}>
              {breakdown.map((row, i) => (
                <div key={i} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:i<breakdown.length-1?`1px solid ${C.border}`:"none"}}>
                  <span style={{fontSize:13,color:C.body}}>{row.label}</span>
                  <span style={{fontSize:13,fontWeight:700,color: row.sign==='+' ? C.blueDark : C.muted,fontVariantNumeric:"tabular-nums"}}>{row.primary}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{fontSize:12,color:C.muted}}>Your target breakdown will appear once your profile is set.</div>
          )}
        </SectionCard>

        {/* 3. HEALTH GOALS (areas of focus, does not drive target) */}
        <SectionCard icon="goal" title="Health Goals" sub="Your areas of focus">
          <MultiChips values={goals} onToggle={toggleGoal} options={GOALS}/>
          <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
            Goals personalize the guidance and content you see. They do not change your hydration target number.
          </div>
        </SectionCard>

        {/* 4. LIFESTYLE (baseline activity level, drives target) */}
        <SectionCard icon="activity" title="Lifestyle" sub="Your baseline activity level">
          <Chips value={lifestyle} onChange={setLifestyle} options={LIFESTYLE}/>
          <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
            This is your day-to-day level, separate from workouts. It adjusts your target.
          </div>
        </SectionCard>

        {/* 5. ACTIVITY (exercise types + minutes, drives target via derived intensity) */}
        <SectionCard icon="pulse" title="Activity" sub="What you do, and how much">
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.body,marginBottom:8}}>Activities you do</div>
              <MultiChips values={activityTypes} onToggle={toggleActivity} options={ACTIVITY_TYPES}/>
            </div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:C.body,marginBottom:8}}>Exercise minutes per day</div>
              <input
                data-testid="exercise-mins-input"
                type="number"
                value={exerciseMins}
                onChange={(e)=>setExerciseMins(e.target.value)}
                placeholder="45"
                style={{width:"100%",padding:"12px 14px",fontSize:15,fontWeight:600,color:C.body,background:C.white,border:`1.5px solid ${C.border}`,borderRadius:12,outline:"none",fontFamily:"Nunito Sans,sans-serif",WebkitAppearance:"none"}}
              />
            </div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.5}}>
              We set your workout intensity from your hardest activity. Right now that reads as
              <span style={{color:C.blueDark,fontWeight:700}}> {derivedEx === "none" ? "no workout load" : derivedEx}</span>, which adjusts your target.
            </div>
          </div>
        </SectionCard>

        {/* 6. CLIMATE (manual now, auto coming, drives target) */}
        <SectionCard icon="climate" title="Climate" sub="Where you live affects your needs">
          <Chips value={climate} onChange={setClimate} options={CLIMATE}/>
          <div style={{fontSize:11,color:C.muted,marginTop:10,lineHeight:1.5}}>
            Set manually for now. Automatic detection from your ZIP is coming. It adjusts your target.
          </div>
        </SectionCard>

        {saveError && <div style={{fontSize:13,fontWeight:600,color:C.danger,textAlign:"center"}}>{saveError}</div>}

        <button
          data-testid="health-save-btn"
          disabled={!hasName || saving}
          onClick={saveHealth}
          style={{
            width:"100%", padding:"15px 0",
            background: (saving||!hasName) ? C.bg : `linear-gradient(135deg,${C.blue},${C.blueDark})`,
            color: (saving||!hasName) ? C.muted : "#fff", border:"none", borderRadius:14,
            fontSize:15, fontWeight:800, cursor: (saving||!hasName) ? "default" : "pointer",
            fontFamily:"inherit", letterSpacing:0.2,
          }}
        >
          {saving ? "Saving..." : savedFlash ? "Saved \u2713  Target updated" : "Save & update my target"}
        </button>

        <div style={{fontSize:11,color:C.muted,textAlign:"center",lineHeight:1.5,padding:"4px 8px"}}>
          Generosity{"\u2122"} estimates hydration and recovery from your inputs and connected devices. It is not medical advice.
        </div>

      </div>
    </div>
  );
}
